import React, { useEffect, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import {
  fetchUploadHistory, loadPastUpload, wipeAllCaches, clearChatHistory,
  fetchAccountLimits, fetchAccountUsage, getUploadComparison,
  clearUploadHistory, restoreUploadHistory
} from '../utils/api';

import '../styles/AccountDashboardPage.css';
import { useNavigate } from 'react-router-dom';

const prettyPlan = (p) => (p === 'core' ? 'Core (Paid)' : p === 'free_trial' ? 'Free Trial' : (p || '—'));
const badge = (txt, bg) => (
  <span style={{ background: bg, color: '#fff', padding: '2px 8px', borderRadius: 999, fontSize: 12 }}>{txt}</span>
);

export default function AccountDashboardPage() {
  const {
    user, uploadedFilename, csvData, token,
    setCsvData, setUploadedFilename, setColumnTypes, setCompanyName,
    setFinancialScale, setForecastData, setStrategyData, setCfoToolsData, setChatbotMessages,
    setHasUploaded, setIsDemo
  } = useAppContext();


  const navigate = useNavigate();

  const [history, setHistory] = useState([]);
  const [limits, setLimits] = useState(null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);

  // NEW: modal state for upload comparison
  const [cmpOpen, setCmpOpen] = useState(false);
  const [cmpLoading, setCmpLoading] = useState(false);
  const [cmpError, setCmpError] = useState('');
  const [cmpData, setCmpData] = useState(null);
  const [cmpDebugOpen, setCmpDebugOpen] = useState(false);
  const [cmpDebug, setCmpDebug] = useState(null);

  const openComparison = async (filename) => {
    if (!token) return alert('Not signed in.');

    // Open modal early so progress + debug are visible
    setCmpOpen(true);
    setCmpLoading(true);
    setCmpError('');
    setCmpData(null);
    setCmpDebugOpen(false);
    setCmpDebug(null);

    const snapshot = () => {
      try { setCmpDebug(window.__lastComparisonFetch || null); } catch {}
    };

    const attempt = async () => {
      const data = await getUploadComparison(filename, token);
      setCmpData(data || {});
      setCmpLoading(false);
      snapshot();
    };

    try {
      await attempt();
    } catch (e) {
      console.error('[Account] getUploadComparison failed (first try)', e);
      snapshot();
      if (e?.status === 404) {
        setCmpError('Generating comparison… retrying once');
        setTimeout(async () => {
          setCmpError('');
          try {
            await attempt();
          } catch (e2) {
            console.error('[Account] getUploadComparison failed (second try)', e2);
            snapshot();
            const msg2 = e2?.body?.detail || e2?.message || 'Failed to fetch comparison.';
            setCmpError(String(msg2));
            setCmpLoading(false);
          }
        }, 1200);
      } else {
        const msg = e?.body?.detail || e?.message || 'Failed to fetch comparison.';
        setCmpError(String(msg));
        setCmpLoading(false);
      }
    }
  };


  const handleUploadRedirect = () => (window.location.href = '/upload');
  const handleTransactionsUploadRedirect = () => (window.location.href = '/transactions-upload');

  const handleWipeCaches = async () => {
    const ok = window.confirm(
      "This will clear cached forecasts, strategy, model insights, dashboard payloads and server memory for the current session.\n\nYou will stay logged in. Continue?"
    );
    if (!ok) return;

    try {
      await wipeAllCaches(token);
      // server now owns visibility; do not set 'historyClearedAt'
      setHistory([]);

      // Reset React app state
      setCsvData(null); setColumnTypes(null); setCompanyName('Your Company');
      setFinancialScale('billions'); setUploadedFilename(null);
      setForecastData?.(null); setStrategyData?.(null); setCfoToolsData?.(null); setChatbotMessages?.([]);

      alert("✅ Caches wiped successfully.");
    } catch (e) {
      console.error("[Wipe Cache] Failed:", e);
      alert("❌ Failed to wipe caches.");
    }
  };

  const handleClearChatbotHistory = async () => {
    if (!user?.id) return alert('No account loaded.');
    if (!window.confirm('Clear your saved chatbot conversation on the server for this account?')) return;
    try {
      await clearChatHistory(user.id, token);
      setChatbotMessages?.([]);
      alert('✅ Chatbot history cleared.');
    } catch (e) {
      console.error('[Account] clearChatbotHistory failed', e);
      alert('❌ Failed to clear chatbot history.');
    }
  };

  const handleManageBilling = async () => {
    if (!token) return alert('Not signed in.');
    setBusy(true);
    try {
      const r = await fetch('/api/billing/portal', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(await r.text());
      const { url } = await r.json();
      if (url) window.location.href = url;
    } catch (e) {
      console.error('[Account] Billing portal failed', e);
      alert('❌ Could not open billing portal.');
    } finally { setBusy(false); }
  };

  const handleCancelAtPeriodEnd = async () => {
    if (!token) return alert('Not signed in.');
    if (!window.confirm("Cancel at period end?\n\nYou will keep access until your current cycle ends, and you won't be charged again.")) return;
    setBusy(true);
    try {
      const r = await fetch("/api/billing/cancel?at_period_end=true", { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(await r.text());
      alert("✅ Your subscription will end at the period end.");
      const refreshed = await fetchAccountLimits(token);
      setLimits(refreshed);
    } catch (e) {
      console.error("[Account] Cancel at period end failed:", e);
      alert("❌ Failed to cancel. Please try again.");
    } finally { setBusy(false); }
  };

  const handleCancelImmediately = async () => {
    if (!token) return alert('Not signed in.');
    if (!window.confirm("Cancel immediately?\n\nThis will stop access right away and you won't be charged again.")) return;
    setBusy(true);
    try {
      const r = await fetch("/api/billing/cancel?at_period_end=false", { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(await r.text());
      alert("✅ Subscription canceled immediately.");
      const refreshed = await fetchAccountLimits(token);
      setLimits(refreshed);
    } catch (e) {
      console.error("[Account] Immediate cancel failed:", e);
      alert("❌ Failed to cancel. Please try again.");
    } finally { setBusy(false); }
  };

  const handleRefreshMembership = async () => {
    if (!token) return alert('Not signed in.');
    setBusy(true);
    try {
      const r = await fetch("/api/account/subscription/sync", { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(await r.text());
      const refreshed = await r.json();
      setLimits(refreshed);
      alert("✅ Membership refreshed from Stripe.");
    } catch (e) {
      console.error("[Account] Refresh membership failed:", e);
      alert("❌ Failed to refresh membership.");
    } finally { setBusy(false); }
  };

  const handleReloadLastUpload = async () => {
    if (!token) return alert('Not signed in.');
    setBusy(true);
    try {
      const r = await fetch("/api/account/last/reload", { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();

      // reflect in app state (authoritative, not demo)
      setCsvData(data?.data || null);
      setColumnTypes(data?.column_types || null);
      setCompanyName(data?.company || 'Unknown company');
      setFinancialScale(data?.scale || data?.unit_scale || 'billions');
      setUploadedFilename(data?.filename || null);

      // flip flags so future boots rehydrate immediately
      try { localStorage.setItem('clairifi:hasUploaded', '1'); } catch {}
      setHasUploaded?.(true);
      setIsDemo?.(false);

      // show Dashboard with fresh data
      navigate('/dashboard');
    } catch (e) {
      console.error('[Account] Reload last upload failed', e);
      alert('❌ Could not reload last upload.');
    } finally { setBusy(false); }
  };
  // Reuse the same state update flow for any payload we load
  const setAppStateFromPayload = (data) => {
    setCsvData(data?.data || null);
    setColumnTypes(data?.column_types || null);
    setCompanyName(data?.company || 'Unknown company');
    setFinancialScale(data?.scale || data?.unit_scale || 'billions');
    setUploadedFilename(data?.filename || null);
    try { localStorage.setItem('clairifi:hasUploaded', '1'); } catch {}
    setHasUploaded?.(true);
    setIsDemo?.(false);
  };

  // Load a specific historical upload
  const handleLoad = async (filename) => {
    if (!token) return alert('Not signed in.');
    setBusy(true);
    try {
      const data = await loadPastUpload(filename, token);
      setAppStateFromPayload(data);
      navigate('/dashboard');
    } catch (e) {
      console.error('[Account] Load past upload failed', e);
      alert('❌ Could not load that upload.');
    } finally {
      setBusy(false);
    }
  };


  // Apply local "hide" filter without nuking everything when timestamps are missing
  const applyLocalFilter = (list) => {
    try {
      const clearedAt = localStorage.getItem('historyClearedAt');
      if (!clearedAt) return list || [];
      const cutoff = new Date(clearedAt).getTime();
      return (list || []).filter((f) => {
        const t = new Date(f.uploaded_at).getTime();
        // If we can't parse the timestamp, don't drop the item
        if (!Number.isFinite(t)) return true;
        return t > cutoff;
      });
    } catch {
      return list || [];
    }
  };

  // One-shot refresher we can call after clearing the local hide flag
  const refreshHistory = async () => {
    if (!token) return;
    try {
      const [listRaw, limitsRaw, usageRaw] = await Promise.all([
        fetchUploadHistory(token).catch(() => []),
        fetchAccountLimits(token).catch(() => null),
        fetchAccountUsage(token).catch(() => null),
      ]);
      setHistory(applyLocalFilter(listRaw));
      setLimits(limitsRaw);
      setUsage(usageRaw);
    } catch {
      // ignore; page shows graceful empty state
    }
  };

  // ------- load on mount -------
  useEffect(() => { refreshHistory(); }, [token]);


  // SWR-style: show cached uploads instantly, then refresh in background
  useEffect(() => {
    if (!user?.id || !token) return;
    try {
      // Lazy-require to avoid import cycles; uses your existing helper
      const { getUploadsWithFallback } = require('../utils/userCache');
      const { value: cached, fresh } = getUploadsWithFallback(
        () => fetchUploadHistory(token),
        user.id,
        (freshList) => setHistory(freshList || [])
      );
      // If cache exists and we haven't shown anything yet, show it immediately
      if (!fresh && Array.isArray(cached) && cached.length) {
        setHistory((h) => (h?.length ? h : cached));
      }
    } catch (e) {
      console.warn('[Account] local uploads mirror unavailable', e);
    }
  }, [user?.id, token]);

  const trialBadge = limits?.plan === 'free_trial'
    ? badge('TRIAL', '#ef4444')
    : (limits?.plan === 'core' ? badge('PAID', '#2563eb') : null);

  return (
    <div className="account-dashboard">
      <h1>Account Dashboard</h1>

      <div className="cta-row">
        <button className="btn btn-primary" onClick={handleUploadRedirect}>Upload CSV</button>
        <button className="btn btn-primary" onClick={handleTransactionsUploadRedirect}>Upload Transactions</button>
      </div>


      <p>Welcome back, <strong>{user?.email}</strong>! {trialBadge}</p>
      <div className="card account-actions">
        <h3>Account</h3>
        <div className="actions">
          <button
            className="btn btn-outline"
            onClick={handleReloadLastUpload}
            disabled={busy}
            title="Re-load your last CSV into the app state"
          >
            Reload last upload
          </button>

          <button
            className="btn btn-outline"
            onClick={handleManageBilling}
            disabled={busy}
            title="Open the Stripe customer portal to manage your subscription"
          >
            Manage billing
          </button>

          <button
            className="btn btn-link btn-danger"
            onClick={handleWipeCaches}
            title="Clears cached analysis, forecasts, dashboard payloads, and server memory"
          >
            Wipe Cache
          </button>

          <button
            className="btn btn-link"
            onClick={handleClearChatbotHistory}
            title="Clears the saved chatbot conversation stored locally for this account"
          >
            Clear Chatbot History
          </button>
        </div>
      </div>

      {limits && (
        <div className="card">
          <h3>Subscription</h3>
          <p><strong>Plan:</strong> {prettyPlan(limits.plan)}</p>
          {limits.plan === "free_trial" && (
            <>
              <p><strong>Trial active:</strong> {String(limits.trial_active)}</p>
              {limits.trial_expires_at && <p><strong>Trial ends:</strong> {new Date(limits.trial_expires_at).toLocaleDateString()}</p>}
            </>
          )}
          <p style={{ margin: '6px 0 12px' }}>
            <strong>Uploads this month:</strong>{" "}
            {typeof limits.uploads_used === 'number' && typeof limits.uploads_limit === 'number'
              ? `${limits.uploads_used} / ${limits.uploads_limit}`
              : (limits.uploads_used ?? '—')}
          </p>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button onClick={handleRefreshMembership} disabled={busy} className="btn btn-outline">
              Refresh membership
            </button>
            <button onClick={handleCancelAtPeriodEnd} disabled={busy} className="btn btn-outline">
              Cancel at period end
            </button>
            <button onClick={handleCancelImmediately} disabled={busy} className="btn btn-outline btn-danger">
              Cancel now
            </button>
          </div>

        </div>
      )}

      <div className="card">
        <h3>Previous Uploads</h3>
        <div className="prev-actions">
          <button
            className="btn"
            onClick={async () => {
              if (!token) return alert('Not signed in.');
              if (!window.confirm("This will permanently clear your previous uploads list for this account.\n\nNew uploads will still appear normally. Continue?")) return;
              try {
                const resp = await clearUploadHistory(token);            // returns { ok: true, uploads_cleared_at: "..." }
                const ts = resp?.uploads_cleared_at || new Date().toISOString();
                try {
                  // Persist the cutoff locally so any cached lists get client-side filtered too
                  localStorage.setItem('historyClearedAt', ts);

                  // If our local uploads mirror exists, clear it (prevents old items flashing back)
                  const uc = require('../utils/userCache');
                  if (uc?.clearUploadsCache && typeof user?.id !== 'undefined') {
                    try { uc.clearUploadsCache(user.id); } catch {}
                  }


                  // Also clear any in-memory API caches
                  const api = require('../utils/api');
                  if (api?.wipeFrontendCaches) { try { api.wipeFrontendCaches(); } catch {} }
                } catch {}
                await refreshHistory();
                alert("✅ Previous uploads cleared for this account.");

              } catch (e) {
                console.error('[Account] clearUploadHistory failed', e);
                alert('❌ Failed to clear previous uploads.');
              }
            }}
          >
            Clear previous uploads
          </button>

          <button
            className="btn"
            onClick={async () => {
              if (!token) return alert('Not signed in.');
              try {
                await restoreUploadHistory(token);
                await refreshHistory();
                alert('✅ All previous uploads restored.');
              } catch (e) {
                console.error('[Account] restoreUploadHistory failed', e);
                alert('❌ Failed to restore previous uploads.');
              }
            }}
          >
            Restore all uploads
          </button>
        </div>





        <ul className="list">
          {(history || []).map((item) => (
            <li
              key={item.filename}
              className="list-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
              }}
            >
              {/* Left side: filename */}
              <span
                className="file-name"
                title={item.filename}
                style={{
                  flex: '1',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.filename}
              </span>

              {/* Right side: timestamp + actions (Load + circular info) */}
              <div className="right-actions">
                <small className="timestamp">
                  {new Date(item.uploaded_at).toLocaleString()}
                </small>

                <button
                  className="btn btn-load"
                  onClick={() => handleLoad(item.filename)}
                  disabled={busy}
                  title="Load this upload into the dashboard"
                >
                  Load
                </button>

                <button
                  className="btn-icon btn-icon-info"
                  aria-label="View comparison summary"
                  title={item?.has_comparison ? 'View comparison summary' : 'Generate & view comparison'}
                  onClick={() => openComparison(item.filename)}
                >
                  <span className="icon-i">i</span>
                </button>
              </div>

            </li>

          ))}
          {(!history || history.length === 0) && <li className="list-item muted">No uploads yet.</li>}
        </ul>


      {/* NEW: Comparison Modal */}
      {cmpOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
          onClick={() => setCmpOpen(false)}
        >
          <div
            className="card"
            style={{ width: 'min(820px, 95vw)', maxHeight: '85vh', overflow: 'auto', padding: 16, background: '#fff' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Comparison Summary</h3>
              <button className="btn btn-link" onClick={() => setCmpOpen(false)}>Close</button>
            </div>

            {cmpLoading && <p className="muted">Loading…</p>}
            {!cmpLoading && cmpError && <p style={{ color: '#b91c1c' }}>❌ {cmpError}</p>}

            {!cmpLoading && !cmpError && cmpData && (
              <>
                {cmpData.summary_text && (
                  <div style={{ margin: '8px 0 16px', whiteSpace: 'pre-wrap' }}>
                    {cmpData.summary_text}
                  </div>
                )}

                {/* Deltas table */}
                {cmpData.diff && typeof cmpData.diff === 'object' && !Array.isArray(cmpData.diff) && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: '6px' }}>Metric</th>
                        <th style={{ textAlign: 'right', borderBottom: '1px solid #e5e7eb', padding: '6px' }}>Prev</th>
                        <th style={{ textAlign: 'right', borderBottom: '1px solid #e5e7eb', padding: '6px' }}>Curr</th>
                        <th style={{ textAlign: 'right', borderBottom: '1px solid #e5e7eb', padding: '6px' }}>Δ</th>
                        <th style={{ textAlign: 'right', borderBottom: '1px solid #e5e7eb', padding: '6px' }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {['revenue','expenses','gross_profit','net_income','margin'].map((k) => {
                        const row = cmpData.diff?.[k];
                        if (!row || typeof row !== 'object') return null;
                        const pct = Number.isFinite(row.pct) ? (row.pct * 100).toFixed(2) + '%' : '—';
                        const format = (v) => (v === null || v === undefined) ? '—' : (k === 'margin' ? (Number(v) * 100).toFixed(2) + '%' : String(v));
                        return (
                          <tr key={k}>
                            <td style={{ padding: '6px', borderBottom: '1px solid #f1f5f9' }}>{k.replace('_',' ')}</td>
                            <td style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #f1f5f9' }}>{format(row.prev)}</td>
                            <td style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #f1f5f9' }}>{format(row.curr)}</td>
                            <td style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #f1f5f9' }}>{format(row.delta)}</td>
                            <td style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #f1f5f9' }}>{pct}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* Metadata */}
                {(cmpData.created_at || cmpData.prev_upload_id) && (
                  <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
                    {cmpData.created_at && <div><strong>Generated:</strong> {new Date(cmpData.created_at).toLocaleString()}</div>}
                    {cmpData.prev_upload_id && <div><strong>Compared with previous upload:</strong> {cmpData.prev_upload_id}</div>}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      </div>
    </div>
  );
}
